import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import toast from 'react-hot-toast'
import { api } from '../utils/api'
import RestaurantSearch from './RestaurantSearch'

const MODES = {
  PICK: 'pick',
  MANUAL: 'manual',
  RECIPE_SEARCH: 'recipe-search',
  RECIPE_PREVIEW: 'recipe-preview',
  RECIPE_URL: 'recipe-url',
  RESTAURANT: 'restaurant',
  RESTAURANT_CONFIRM: 'restaurant-confirm',
  PINTEREST_BOARDS: 'pinterest-boards',
  PINTEREST_PINS: 'pinterest-pins',
}

export default function AddMealLibraryModal({ onClose, onAdded }) {
  const [mode, setMode] = useState(MODES.PICK)
  const [loading, setLoading] = useState(false)

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [notes, setNotes] = useState('')

  const [recipeQuery, setRecipeQuery] = useState('')
  const [recipePreview, setRecipePreview] = useState(null)
  const [recipeIndex, setRecipeIndex] = useState(0)
  const [recipeTotalResults, setRecipeTotalResults] = useState(0)
  const [expandIngredients, setExpandIngredients] = useState(false)
  const [expandInstructions, setExpandInstructions] = useState(false)

  const [recipeUrl, setRecipeUrl] = useState('')
  const [selectedRestaurant, setSelectedRestaurant] = useState(null)

  const [pinterestBoards, setPinterestBoards] = useState([])
  const [selectedBoard, setSelectedBoard] = useState(null)
  const [pinterestPins, setPinterestPins] = useState([])
  const [pinsBookmark, setPinsBookmark] = useState(null)
  const [pinterestConnected, setPinterestConnected] = useState(null)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('pinterest') === 'connected') {
      toast.success('Pinterest connected!')
      window.history.replaceState({}, '', window.location.pathname)
    } else if (params.get('pinterest') === 'denied') {
      toast.error('Pinterest access was denied')
      window.history.replaceState({}, '', window.location.pathname)
    }
  }, [])

  async function handleAddManual(e) {
    e.preventDefault()
    if (!name.trim()) return
    setLoading(true)
    try {
      const result = await api.createMeal({ type: 'manual', name: name.trim(), description: description.trim() || null, notes: notes.trim() || null })
      toast.success('Meal added!')
      onAdded(result.meal)
    } catch (err) {
      toast.error(err.message)
    } finally {
      setLoading(false)
    }
  }

  async function handleRecipeSearch(index = 0) {
    if (!recipeQuery.trim()) return
    setLoading(true)
    try {
      toast.loading(index === 0 ? 'Searching for recipe...' : 'Finding next result...', { id: 'lib-search' })
      const result = await api.recipeSearch(recipeQuery.trim(), index)
      setRecipePreview(result.preview)
      setRecipeIndex(result.currentIndex)
      setRecipeTotalResults(result.totalResults)
      toast.dismiss('lib-search')
      setMode(MODES.RECIPE_PREVIEW)
    } catch (err) {
      toast.error(err.message || 'Recipe search failed', { id: 'lib-search' })
    } finally {
      setLoading(false)
    }
  }

  async function handleNextRecipe() {
    await handleRecipeSearch(recipeIndex + 1)
  }

  async function handleConfirmRecipePreview() {
    if (!recipePreview) return
    setLoading(true)
    try {
      toast.loading('Saving recipe...', { id: 'lib-save' })
      const result = await api.createMeal({
        type: 'imported',
        url: recipePreview.sourceUrl,
        name: recipePreview.name,
        summary: recipePreview.summary,
        ingredients: recipePreview.ingredients,
        instructions: recipePreview.instructions,
        prepTime: recipePreview.prepTime,
        cookTime: recipePreview.cookTime,
        servings: recipePreview.servings,
      })
      toast.success('Recipe saved!', { id: 'lib-save' })
      onAdded(result.meal)
    } catch (err) {
      toast.error(err.message || 'Failed to save recipe', { id: 'lib-save' })
    } finally {
      setLoading(false)
    }
  }

  async function handleImportUrl() {
    if (!recipeUrl.trim()) return
    setLoading(true)
    try {
      toast.loading('Importing recipe...', { id: 'lib-import' })
      const result = await api.createMeal({ type: 'imported', url: recipeUrl.trim() })
      toast.success('Recipe imported!', { id: 'lib-import' })
      onAdded(result.meal)
    } catch (err) {
      toast.error(err.message || 'Failed to import recipe', { id: 'lib-import' })
    } finally {
      setLoading(false)
    }
  }

  async function handleSelectRestaurant(place) {
    setLoading(true)
    try {
      const result = await api.createMeal({
        type: 'restaurant',
        placeId: place.placeId,
        name: place.name,
        address: place.address,
        mapsUrl: place.mapsUrl,
        phoneNumber: place.phoneNumber,
        website: place.website,
      })
      setSelectedRestaurant(result.meal)
      setMode(MODES.RESTAURANT_CONFIRM)
    } catch (err) {
      toast.error(err.message)
    } finally {
      setLoading(false)
    }
  }

  async function handleOpenPinterest() {
    setMode(MODES.PINTEREST_BOARDS)
    setLoading(true)
    try {
      const result = await api.pinterestBoards()
      setPinterestBoards(result.boards)
      setPinterestConnected(true)
    } catch (err) {
      if (err.message?.includes('PINTEREST_NOT_CONNECTED') || err.message?.includes('not connected')) {
        setPinterestConnected(false)
      } else if (err.message?.includes('PINTEREST_TOKEN_EXPIRED')) {
        setPinterestConnected(false)
        toast.error('Pinterest session expired - please reconnect')
      } else {
        toast.error(err.message || 'Failed to load Pinterest boards')
        setMode(MODES.PICK)
      }
    } finally {
      setLoading(false)
    }
  }

  function handleConnectPinterest() {
    const token = localStorage.getItem("dinner_token"); window.location.href = `${import.meta.env.VITE_API_URL}/pinterest/auth?token=${token}`
  }

  async function handleSelectBoard(board) {
    setSelectedBoard(board)
    setMode(MODES.PINTEREST_PINS)
    setPinterestPins([])
    setPinsBookmark(null)
    setLoading(true)
    try {
      const result = await api.pinterestPins(board.id)
      setPinterestPins(result.pins)
      setPinsBookmark(result.bookmark)
    } catch (err) {
      toast.error(err.message || 'Failed to load pins')
      setMode(MODES.PINTEREST_BOARDS)
    } finally {
      setLoading(false)
    }
  }

  async function handleLoadMorePins() {
    if (!pinsBookmark || !selectedBoard) return
    setLoading(true)
    try {
      const result = await api.pinterestPins(selectedBoard.id, pinsBookmark)
      setPinterestPins(prev => [...prev, ...result.pins])
      setPinsBookmark(result.bookmark)
    } catch (err) {
      toast.error(err.message || 'Failed to load more pins')
    } finally {
      setLoading(false)
    }
  }

  async function handleSelectPin(pin) {
    if (!pin.link) {
      toast.error('This pin does not have a source URL')
      return
    }
    setLoading(true)
    try {
      toast.loading('Importing recipe from pin...', { id: 'pin-import' })
      const result = await api.createMeal({ type: 'imported', url: pin.link })
      toast.success('Recipe imported!', { id: 'pin-import' })
      onAdded(result.meal)
    } catch (err) {
      toast.error(err.message || 'Failed to import recipe from pin', { id: 'pin-import' })
    } finally {
      setLoading(false)
    }
  }

  function goBack() {
    if (mode === MODES.RECIPE_PREVIEW) {
      setMode(MODES.RECIPE_SEARCH)
      setRecipePreview(null)
      setExpandIngredients(false)
      setExpandInstructions(false)
    } else if (mode === MODES.PINTEREST_PINS) {
      setMode(MODES.PINTEREST_BOARDS)
      setSelectedBoard(null)
      setPinterestPins([])
    } else {
      setMode(MODES.PICK)
      setSelectedRestaurant(null)
      setPinterestConnected(null)
    }
  }
  return createPortal(
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
      <div className="fixed top-4 left-0 right-0 max-w-2xl mx-auto bg-white rounded-2xl z-50 flex flex-col" style={{ maxHeight: 'calc(100vh - 2rem)' }}>
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 bg-gray-300 rounded-full" />
        </div>
        <div className="px-4 pb-3 flex items-center justify-between border-b border-gray-100">
          <div>
            {mode !== MODES.PICK && (
              <button onClick={goBack} className="text-sm text-gray-400 hover:text-gray-600 mb-1">← Back</button>
            )}
            <p className="font-semibold text-gray-900">
              {mode === MODES.PINTEREST_BOARDS && 'Pinterest Boards'}
              {mode === MODES.PINTEREST_PINS && (selectedBoard?.name || 'Pins')}
              {![MODES.PINTEREST_BOARDS, MODES.PINTEREST_PINS].includes(mode) && 'Add to meal library'}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">✕</button>
        </div>

        {loading && (
          <div className="absolute inset-0 bg-white/80 z-10 flex flex-col items-center justify-center gap-3 rounded-t-2xl">
            <div className="w-10 h-10 border-4 border-green-200 border-t-green-600 rounded-full animate-spin" />
            <p className="text-sm text-gray-500 font-medium">Please wait...</p>
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          {mode === MODES.PICK && (
            <div className="p-4 flex flex-col gap-3">
              {[
                { mode: MODES.MANUAL, icon: '🍳', title: 'Manual entry', desc: 'Type in a meal name and notes' },
                { mode: MODES.RECIPE_SEARCH, icon: '🔍', title: 'Search online recipes', desc: 'Describe what you want, AI finds and imports the best result' },
                { mode: MODES.RECIPE_URL, icon: '🌐', title: 'Import from URL', desc: 'Paste a recipe link, AI extracts the details' },
                { mode: MODES.RESTAURANT, icon: '🥡', title: 'Add restaurant', desc: 'Search Richmond-area carryout options' },
              ].map(opt => (
                <button key={opt.mode} onClick={() => setMode(opt.mode)}
                  className="w-full text-left p-4 rounded-xl border-2 border-gray-200 hover:border-green-400 hover:bg-green-50 transition-colors">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">{opt.icon}</span>
                    <div>
                      <p className="font-medium text-gray-900">{opt.title}</p>
                      <p className="text-sm text-gray-500 mt-0.5">{opt.desc}</p>
                    </div>
                  </div>
                </button>
              ))}
              <button onClick={handleOpenPinterest}
                className="w-full text-left p-4 rounded-xl border-2 border-gray-200 hover:border-red-300 hover:bg-red-50 transition-colors">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">📌</span>
                  <div>
                    <p className="font-medium text-gray-900">Browse Pinterest boards</p>
                    <p className="text-sm text-gray-500 mt-0.5">Tap a saved pin to import the recipe</p>
                  </div>
                </div>
              </button>
            </div>
          )}

          {mode === MODES.MANUAL && (
            <form onSubmit={handleAddManual} className="p-4 flex flex-col gap-3">
              <div>
                <label className="text-sm font-medium text-gray-700">Meal name *</label>
                <input type="text" value={name} onChange={e => setName(e.target.value)} autoFocus required
                  placeholder="e.g. Chicken Tikka Masala"
                  className="w-full mt-1 border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-green-500" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Description (optional)</label>
                <input type="text" value={description} onChange={e => setDescription(e.target.value)}
                  placeholder="Brief description"
                  className="w-full mt-1 border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-green-500" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Notes (optional)</label>
                <textarea value={notes} onChange={e => setNotes(e.target.value)}
                  placeholder="Recipe notes, ingredient tips, etc."
                  rows={3}
                  className="w-full mt-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 resize-none" />
              </div>
              <button type="submit" disabled={loading || !name.trim()} className="btn-primary w-full py-3 text-base">
                {loading ? 'Adding...' : 'Add to library'}
              </button>
            </form>
          )}

          {mode === MODES.RECIPE_SEARCH && (
            <div className="p-4 flex flex-col gap-3">
              <div>
                <label className="text-sm font-medium text-gray-700">What are you looking for?</label>
                <div className="flex gap-2 mt-1">
                  <input
                    type="text"
                    value={recipeQuery}
                    onChange={e => setRecipeQuery(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && !loading && handleRecipeSearch(0)}
                    placeholder="e.g. split pea soup crockpot"
                    autoFocus
                    className="flex-1 border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                  <button
                    type="button"
                    onClick={() => handleRecipeSearch(0)}
                    disabled={loading || !recipeQuery.trim()}
                    className="btn-primary px-4 py-2.5 rounded-lg disabled:opacity-50 flex items-center justify-center min-w-[44px]"
                  >
                    {loading ? <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : '🔍'}
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-1">We will find the top result and show you a preview before saving</p>
              </div>
            </div>
          )}
          {mode === MODES.RECIPE_PREVIEW && recipePreview && (
            <div className="p-4 flex flex-col gap-4">
              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <p className="font-bold text-gray-900 text-lg">{recipePreview.name}</p>
                {recipePreview.summary && <p className="text-sm text-gray-600 mt-1">{recipePreview.summary}</p>}
                <div className="flex flex-wrap gap-3 mt-3">
                  {recipePreview.prepTime && <span className="text-xs bg-white border border-green-200 rounded-full px-3 py-1 text-gray-600">⏱ Prep: {recipePreview.prepTime}</span>}
                  {recipePreview.cookTime && <span className="text-xs bg-white border border-green-200 rounded-full px-3 py-1 text-gray-600">🔥 Cook: {recipePreview.cookTime}</span>}
                  {recipePreview.servings && <span className="text-xs bg-white border border-green-200 rounded-full px-3 py-1 text-gray-600">👥 {recipePreview.servings}</span>}
                </div>
                {recipePreview.sourceUrl && (
                  <a href={recipePreview.sourceUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-green-600 underline mt-2 block truncate">
                    {recipePreview.sourceUrl}
                  </a>
                )}
                {recipeIndex + 1 < recipeTotalResults && (
                  <button onClick={handleNextRecipe} disabled={loading}
                    className="mt-3 w-full text-sm border border-green-300 text-green-700 rounded-lg py-2 hover:bg-green-50 disabled:opacity-50 flex items-center justify-center gap-2">
                    {loading ? 'Loading...' : `Try next result (${recipeIndex + 1} of ${recipeTotalResults})`}
                  </button>
                )}
              </div>
              {recipePreview.ingredients?.length > 0 && (
                <div className="border border-gray-200 rounded-xl overflow-hidden">
                  <button onClick={() => setExpandIngredients(v => !v)} className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 text-sm font-medium text-gray-700">
                    <span>🥕 Ingredients ({recipePreview.ingredients.length})</span>
                    <span>{expandIngredients ? '▲' : '▼'}</span>
                  </button>
                  {expandIngredients && (
                    <ul className="px-4 py-3 flex flex-col gap-1">
                      {recipePreview.ingredients.map((ing, i) => (
                        <li key={i} className="text-sm text-gray-700 flex gap-2"><span className="text-gray-400">•</span><span>{ing}</span></li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              {recipePreview.instructions?.length > 0 && (
                <div className="border border-gray-200 rounded-xl overflow-hidden">
                  <button onClick={() => setExpandInstructions(v => !v)} className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 text-sm font-medium text-gray-700">
                    <span>📋 Instructions ({recipePreview.instructions.length} steps)</span>
                    <span>{expandInstructions ? '▲' : '▼'}</span>
                  </button>
                  {expandInstructions && (
                    <ol className="px-4 py-3 flex flex-col gap-2">
                      {recipePreview.instructions.map((step, i) => (
                        <li key={i} className="text-sm text-gray-700 flex gap-3">
                          <span className="font-bold text-green-600 shrink-0">{i + 1}.</span>
                          <span>{step}</span>
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
              )}
              <button onClick={() => { setMode(MODES.RECIPE_SEARCH); setRecipePreview(null); setRecipeIndex(0) }} className="text-sm text-gray-400 hover:text-gray-600 text-center">
                Search for something different
              </button>
              <button onClick={handleConfirmRecipePreview} disabled={loading} className="btn-primary w-full py-3 text-base">
                {loading ? 'Saving...' : 'Save to library'}
              </button>
            </div>
          )}

          {mode === MODES.RECIPE_URL && (
            <div className="p-4 flex flex-col gap-3">
              <div>
                <label className="text-sm font-medium text-gray-700">Recipe URL</label>
                <input type="url" value={recipeUrl} onChange={e => setRecipeUrl(e.target.value)} autoFocus
                  placeholder="https://www.allrecipes.com/recipe/..."
                  className="w-full mt-1 border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-green-500" />
                <p className="text-xs text-gray-400 mt-1">We will use AI to extract the recipe name, ingredients, and steps.</p>
              </div>
              <button onClick={handleImportUrl} disabled={loading || !recipeUrl.trim()} className="btn-primary w-full py-3 text-base">
                {loading ? 'Importing...' : 'Import recipe'}
              </button>
            </div>
          )}

          {mode === MODES.RESTAURANT && (
            <RestaurantSearch onSelect={handleSelectRestaurant} loading={loading} />
          )}

          {mode === MODES.RESTAURANT_CONFIRM && selectedRestaurant && (
            <div className="p-4 flex flex-col gap-3">
              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-2xl">🥡</span>
                  <p className="font-semibold text-gray-900">{selectedRestaurant.name}</p>
                </div>
                {selectedRestaurant.address && <p className="text-sm text-gray-600">📍 {selectedRestaurant.address}</p>}
                {selectedRestaurant.website && <a href={selectedRestaurant.website} target="_blank" rel="noopener noreferrer" className="text-xs text-green-600 underline mt-1 block">{selectedRestaurant.website}</a>}
              </div>
              <button onClick={() => onAdded(selectedRestaurant)} className="btn-primary w-full py-3 text-base">
                Save to library
              </button>
            </div>
          )}

          {mode === MODES.PINTEREST_BOARDS && pinterestConnected === false && (
            <div className="p-6 flex flex-col items-center gap-4 text-center">
              <span className="text-5xl">📌</span>
              <p className="font-semibold text-gray-900">Connect your Pinterest account</p>
              <p className="text-sm text-gray-500">Tap below to authorize the app to read your boards. You will be redirected to Pinterest and then brought back here.</p>
              <button onClick={handleConnectPinterest} className="btn-primary w-full py-3 text-base">
                Connect Pinterest
              </button>
            </div>
          )}

          {mode === MODES.PINTEREST_BOARDS && pinterestConnected === true && (
            <div className="p-4 flex flex-col gap-3">
              {pinterestBoards.length === 0 && !loading && (
                <p className="text-center text-gray-500 text-sm py-8">No boards found</p>
              )}
              {pinterestBoards.map(board => (
                <button key={board.id} onClick={() => handleSelectBoard(board)}
                  className="w-full text-left p-4 rounded-xl border-2 border-gray-200 hover:border-red-300 hover:bg-red-50 transition-colors flex items-center gap-3">
                  {board.coverImageUrl ? (
                    <img src={board.coverImageUrl} alt={board.name} className="w-12 h-12 rounded-lg object-cover shrink-0" />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-red-100 flex items-center justify-center shrink-0">
                      <span className="text-xl">📌</span>
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 truncate">{board.name}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{board.pinCount} pins</p>
                  </div>
                  <span className="text-gray-300 ml-auto shrink-0">›</span>
                </button>
              ))}
            </div>
          )}

          {mode === MODES.PINTEREST_PINS && (
            <div className="p-4">
              {pinterestPins.length === 0 && !loading && (
                <p className="text-center text-gray-500 text-sm py-8">No pins found in this board</p>
              )}
              <div className="grid grid-cols-2 gap-3">
                {pinterestPins.map(pin => (
                  <button key={pin.id} onClick={() => handleSelectPin(pin)}
                    className="text-left rounded-xl border border-gray-200 overflow-hidden hover:border-red-300 hover:shadow-md transition-all active:scale-95">
                    {pin.imageUrl ? (
                      <img src={pin.imageUrl} alt={pin.title || 'Pin'} className="w-full aspect-[4/3] object-cover" />
                    ) : (
                      <div className="w-full aspect-[4/3] bg-red-50 flex items-center justify-center">
                        <span className="text-3xl">📌</span>
                      </div>
                    )}
                    <div className="p-2">
                      <p className="text-xs font-medium text-gray-800 line-clamp-2">{pin.title || pin.description || 'Untitled pin'}</p>
                      {pin.link && (
                        <p className="text-xs text-gray-400 mt-0.5 truncate">{new URL(pin.link).hostname.replace('www.', '')}</p>
                      )}
                    </div>
                  </button>
                ))}
              </div>
              {pinsBookmark && (
                <button onClick={handleLoadMorePins} disabled={loading}
                  className="mt-4 w-full py-3 text-sm border border-gray-300 rounded-xl text-gray-600 hover:bg-gray-50 disabled:opacity-50">
                  {loading ? 'Loading...' : 'Load more pins'}
                </button>
              )}
              <p className="text-xs text-gray-400 text-center mt-3">Tap a pin to import the recipe</p>
            </div>
          )}

        </div>
      </div>
    </>,
  document.body
  )
}
